import { useState, useEffect, useCallback } from "react";
import { Handshake, Loader2, Download, Trash2, FileSignature, Sparkles, Link2, Mail, CheckCircle2, Clock } from "lucide-react";
import { API, apiFetch, downloadFile } from "@/portal/api";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";

const empty = {
  coop_type: "influencer", partner_name: "", partner_ico: "", partner_address: "",
  partner_email: "", partner_phone: "", subject: "", deliverables: "", compensation: "",
  duration: "", place: "Praha", note: "",
};

const CooperationSection = ({ token }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [dl, setDl] = useState(null);

  const load = useCallback(async () => {
    try { setList(await apiFetch("/admin/cooperation", { token })); } catch (e) { /* ignore */ }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const isInfluencer = form.coop_type === "influencer";

  const generate = async () => {
    if (!form.partner_name.trim()) { toast.error("Vyplňte jméno / název partnera."); return; }
    setBusy(true);
    try {
      const rec = await apiFetch("/admin/cooperation/generate", { method: "POST", token, body: form });
      toast.success("Smlouva o spolupráci vytvořena.");
      await load();
      downloadFile(`/admin/cooperation/${rec.id}/pdf`, token, `smlouva-spoluprace-${rec.id.slice(0, 6)}.pdf`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!(await confirmDialog({ title: "Smazat smlouvu?", description: "Vygenerovanou smlouvu o spolupráci nelze obnovit." }))) return;
    try { await apiFetch(`/admin/cooperation/${id}`, { method: "DELETE", token }); load(); }
    catch (e) { toast.error(e.message); }
  };

  const grab = async (id) => {
    setDl(id);
    try { await downloadFile(`/admin/cooperation/${id}/pdf`, token, `smlouva-spoluprace-${id.slice(0, 6)}.pdf`); }
    catch (e) { toast.error(e.message); } finally { setDl(null); }
  };

  const copyLink = (c) => {
    const url = `${window.location.origin}/podpis-spoluprace/${c.sign_token}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Odkaz k podpisu zkopírován."),
      () => toast.error("Nepodařilo se zkopírovat odkaz.")
    );
  };

  const sendEmail = async (c) => {
    try {
      const r = await apiFetch(`/admin/cooperation/${c.id}/send-sign`, { method: "POST", token });
      toast[r.email_sent ? "success" : "error"](r.email_sent ? "E-mail s odkazem odeslán partnerovi." : "E-mail se nepodařilo odeslat (zkontrolujte e-mail partnera).");
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="glass rounded-2xl p-5 mb-4 border border-orange-500/20" data-testid="cooperation-section">
      <div className="flex items-center gap-2 mb-1">
        <Handshake className="w-4 h-4 text-orange-400" />
        <span className="font-semibold">Smlouva o spolupráci</span>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Vytvořte smlouvu s influencerem/ambasadorem nebo obchodním partnerem. Vyplňte a stáhněte PDF.</p>

      {/* Type toggle */}
      <div className="inline-flex rounded-lg border border-white/10 bg-white/5 p-0.5 mb-4" data-testid="coop-type">
        {[["influencer", "Influencer / ambasador"], ["partner", "Obchodní partner"]].map(([val, lbl]) => (
          <button key={val} type="button" onClick={() => setForm({ ...form, coop_type: val })}
            className={`px-3 py-1.5 text-xs rounded-md transition-colors ${form.coop_type === val ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`}
            data-testid={`coop-type-${val}`}>{lbl}</button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <input className="input-dark" placeholder="Jméno / název partnera *" value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} data-testid="coop-name" />
        <input className="input-dark" placeholder="IČO / datum narození" value={form.partner_ico} onChange={(e) => setForm({ ...form, partner_ico: e.target.value })} data-testid="coop-ico" />
        <input className="input-dark" placeholder="Adresa" value={form.partner_address} onChange={(e) => setForm({ ...form, partner_address: e.target.value })} data-testid="coop-address" />
        <input className="input-dark" placeholder="E-mail" value={form.partner_email} onChange={(e) => setForm({ ...form, partner_email: e.target.value })} data-testid="coop-email" />
        <input className="input-dark" placeholder="Telefon" value={form.partner_phone} onChange={(e) => setForm({ ...form, partner_phone: e.target.value })} data-testid="coop-phone" />
        <input className="input-dark" placeholder="Místo podpisu" value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} data-testid="coop-place" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <input className="input-dark" placeholder={isInfluencer ? "Předmět (např. propagace na IG/TikTok)" : "Předmět (např. doporučování zákazníků)"} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="coop-subject" />
        <input className="input-dark" placeholder={isInfluencer ? "Výstupy (např. 3 reels + 5 stories/měs.)" : "Rozsah (např. předávání kontaktů)"} value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })} data-testid="coop-deliverables" />
        <input className="input-dark" placeholder={isInfluencer ? "Odměna (např. vůz zdarma na 3 měs.)" : "Provize (např. 3 000 Kč / obchod)"} value={form.compensation} onChange={(e) => setForm({ ...form, compensation: e.target.value })} data-testid="coop-compensation" />
        <input className="input-dark" placeholder="Doba spolupráce (např. 6 měsíců)" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="coop-duration" />
      </div>
      <textarea rows={2} className="input-dark w-full mt-3 resize-none" placeholder="Další ujednání (nepovinné)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="coop-note" />

      <button onClick={generate} disabled={busy} className="btn-accent mt-3 inline-flex items-center gap-2 disabled:opacity-60" data-testid="coop-generate">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Vytvořit smlouvu (PDF)
      </button>

      {list.length > 0 && (
        <div className="mt-5 border-t border-white/10 pt-4">
          <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">Vytvořené smlouvy o spolupráci</p>
          <div className="space-y-2" data-testid="coop-list">
            {list.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 rounded-lg bg-white/5 px-3 py-2 text-sm flex-wrap">
                <span className="inline-flex items-center gap-2 text-zinc-200">
                  <FileSignature className="w-4 h-4 text-orange-400" /> {c.partner_name}
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-300">{c.coop_type === "influencer" ? "Influencer" : "Partner"}</span>
                  {c.status === "signed" ? (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 inline-flex items-center gap-1" data-testid={`coop-status-${c.id}`}><CheckCircle2 className="w-3 h-3" /> Podepsáno</span>
                  ) : (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 inline-flex items-center gap-1" data-testid={`coop-status-${c.id}`}><Clock className="w-3 h-3" /> Čeká na podpis</span>
                  )}
                </span>
                <div className="flex gap-2 shrink-0">
                  {c.status !== "signed" && (
                    <>
                      <button onClick={() => copyLink(c)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-white/10 text-zinc-200 hover:bg-white/20" data-testid={`coop-copylink-${c.id}`} title="Kopírovat odkaz k podpisu">
                        <Link2 className="w-3 h-3" /> Odkaz
                      </button>
                      <button onClick={() => sendEmail(c)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-white/10 text-zinc-200 hover:bg-white/20" data-testid={`coop-email-${c.id}`} title="Odeslat odkaz e-mailem">
                        <Mail className="w-3 h-3" />
                      </button>
                    </>
                  )}
                  <button onClick={() => grab(c.id)} disabled={dl === c.id} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60" data-testid={`coop-download-${c.id}`}>
                    {dl === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PDF
                  </button>
                  <button onClick={() => del(c.id)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-red-500/20 text-red-200 hover:bg-red-500/40" data-testid={`coop-delete-${c.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CooperationSection;
